/**
 * O catalogo de medicao: o nome de cada tipo, as metricas do painel e as faixas.
 *
 * E a fonte unica de nome do painel - cabecalho de tabela, seletor de grafico,
 * cartao, filtro de indicador e editor de faixas saem todos daqui.
 */
/**
 * O nome de cada metrica, por id de catalogo. UMA entrada por metrica.
 *
 * Esta e a fonte unica de nome do painel: os cabecalhos de tabela, os dois
 * seletores do grafico, os cartoes, o filtro de indicador e o editor de faixas
 * todos saem daqui. Antes eram QUATRO vocabularios para a mesma coisa - a pressao
 * se chamava "Pressão" na tabela e no seletor de linha e "Pressão Arterial" no
 * cartao e no seletor de barras -, e seis das dez metricas tinham mais de um nome.
 *
 * Os nomes dizem DE QUE se trata, e nao a palavra mais curta que cabia: "pressao"
 * sozinho serve para atmosferica, "temperatura" sozinho nao distingue do tipo 152
 * ("Temperatura do Dedo", que existe no catalogo). Ver B-03 em `demandas/`.
 *
 * NAO e o nome do BACKEND. O `measurement_type.measure_name` viaja no
 * `patient/{id}/history` junto com o `measurementTypeId`, e quem le ja prefere o
 * ID - o app e o painel antigo casam pelo numero e usam o rotulo so como
 * retaguarda, desde que renomear o 102 em 25/08/2026 quebrou os dois. O painel
 * nao le mais aquele nome em lugar nenhum: o `measurementTypeName` que vem na
 * resposta e ignorado, e o nome sai daqui pelo id. Ver B-14 em `demandas/`.
 */
export const MEASUREMENT_NAMES = {
    102: 'Pressão Arterial',
    103: 'Temperatura Corporal',
    153: 'Frequência Cardíaca',
    154: 'Variabilidade Cardíaca (HRV)',
    155: 'Oxigenação do Sangue',
    156: 'Estresse',
    201: 'Passos',
    202: 'Gasto Calórico',
    203: 'Distância',
    204: 'Sono',
    205: 'Frequência Cardíaca em Atividade'
};
/** O nome de tela de um tipo de medicao. Sem nome, o id - que ao menos identifica. */
export function measurementLabelOf(id) {
    return MEASUREMENT_NAMES[id] ?? String(id);
}
/**
 * O id de catalogo de cada metrica do painel.
 *
 * E o elo entre os dois vocabularios do sistema: o painel fala em `MetricType`
 * (porque e o que os endpoints de analytics aceitam) e as tabelas falam em id de
 * tipo de medicao. Com o elo, o nome sai de um lugar so.
 *
 * A ORDEM das chaves e a do `METRIC_TYPES`, que monta o seletor do painel do
 * paciente - trocar a ordem aqui troca a ordem la.
 */
export const METRIC_TYPE_IDS = {
    FREQUENCIA_CARDIACA: 153,
    HRV: 154,
    OXIGENACAO: 155,
    TEMPERATURA: 103,
    SONO: 204,
    PRESSAO: 102
};
export const METRIC_TYPES = Object.keys(METRIC_TYPE_IDS);
/**
 * O codigo de cada metrica no endereco. Ver `CodeTable`.
 *
 * Nao e o id de catalogo (153, 154, ...) de proposito: aquele tem tres digitos e
 * este tem um, e o endereco carrega a metrica em quatro parametros diferentes.
 */
export const METRIC_CODES = {
    '0': 'FREQUENCIA_CARDIACA',
    '1': 'HRV',
    '2': 'OXIGENACAO',
    '3': 'TEMPERATURA',
    '4': 'SONO',
    '5': 'PRESSAO'
};
export const METRIC_LABELS = Object.fromEntries(METRIC_TYPES.map((metric) => [metric, measurementLabelOf(METRIC_TYPE_IDS[metric])]));
function metricOptions(order) {
    return order.map((value) => ({ value, label: METRIC_LABELS[value] }));
}
/**
 * O seletor do grafico de LINHA, na ordem do painel antigo (`METRIC_OPTIONS` em
 * `pages/Dashboard/index.tsx`).
 *
 * So a ORDEM e dele. Os rotulos saem do `METRIC_LABELS`: os dois seletores eram
 * duas listas escritas a mao, e por isso discordavam entre si.
 */
export const LINE_METRIC_OPTIONS = metricOptions([
    'FREQUENCIA_CARDIACA',
    'HRV',
    'OXIGENACAO',
    'TEMPERATURA',
    'SONO',
    'PRESSAO'
]);
/** O seletor do grafico de BARRAS - a ordem do `BAR_CHART_METRIC_OPTIONS` de la. */
export const BAR_METRIC_OPTIONS = metricOptions([
    'SONO',
    'FREQUENCIA_CARDIACA',
    'OXIGENACAO',
    'TEMPERATURA',
    'HRV',
    'PRESSAO'
]);
/**
 * Como cada faixa se chama na tela.
 *
 * ## "Crítico" saiu, e por pedido do cliente
 *
 * A ata de 15/09/2026 termina com "nao usar o termo critico pois causa confusao;
 * usar o termo Atencao". O ENUM nao muda - `CRITICAL` e contrato lido pelo app -, e
 * a troca fica toda aqui, que e a camada de exibicao.
 *
 * Renomear so o pior nivel poria a palavra e a cor em desacordo: ambar e a cor de
 * atencao em qualquer painel, e ambar era o `FAIR`/"Razoavel". Por isso os DOIS
 * mudaram - `FAIR` virou "Regular", que e a escala canonica em portugues
 * (otimo/bom/regular) e e neutra.
 *
 * ## O `UNKNOWN` ganhou nome proprio
 *
 * Ele significa "nenhuma faixa configurada para este valor", e dizia "Critico" -
 * uma medicao sem parametro definido aparecia como se fosse perigosa. Agora diz o
 * que e. Note que no GRAFICO ele continua somado ao `CRITICAL`, como sempre esteve:
 * tirar dali encolheria uma barra que o cliente ja conhece, sem a base ter mudado.
 */
export const STATUS_LABELS = {
    EXCELLENT: 'Ótimo',
    GOOD: 'Bom',
    FAIR: 'Regular',
    CRITICAL: 'Atenção',
    UNKNOWN: 'Sem faixa definida'
};
/**
 * A cor de cada faixa, em HEXA e nao em token de CSS.
 *
 * Fica na APRESENTACAO, e nao na folha de estilo, porque quem a consome sao os
 * graficos: a barra e o ponto recebem a cor como VALOR, por atributo, e um
 * `var(--ok)` ali chegaria como texto que o canvas nao sabe pintar.
 *
 * As quatro sao a paleta do app (`MormaiiColors.kt`), as mesmas do tema em
 * `renderer-solid/src/styles/theme.css`: azul informativo, verde, ambar e coral. Eram
 * o roxo `#5141E5` e tres tons genericos ate 23/09/2026, quando o painel adotou a pele
 * do app.
 *
 * `EXCELLENT` NAO e o ciano do acento de proposito: o acento marca o que se clica e a
 * linha do grafico, e se ele tambem significasse "otimo" as duas leituras competiriam
 * no mesmo cartao. No desenho do cliente aquela barra e azul.
 */
export const STATUS_COLORS = {
    EXCELLENT: '#4A9FE7',
    GOOD: '#6FE56A',
    FAIR: '#F2B341',
    CRITICAL: '#FF6B6B',
    UNKNOWN: '#FF6B6B'
};
/**
 * As mesmas faixas como COR DE TELA: variaveis do tema, e nao hexadecimais.
 *
 * Desde 08/10/2026 o painel tem tres temas, e no Claro o verde e o ambar de
 * `STATUS_COLORS` somem no fundo branco - cada tema define a sua versao em `theme.css`
 * (`--status-*`). E isto que vai para o scope de quem desenha na tela. O PDF, que e
 * papel e nao tem tema, continua lendo `STATUS_COLORS`.
 */
export const STATUS_TONES = {
    EXCELLENT: 'var(--status-excellent)',
    GOOD: 'var(--status-good)',
    FAIR: 'var(--status-fair)',
    CRITICAL: 'var(--status-critical)',
    UNKNOWN: 'var(--status-critical)'
};
/** A ordem em que as faixas aparecem no grafico, da melhor para a pior. */
export const STATUS_ORDER = ['EXCELLENT', 'GOOD', 'FAIR', 'CRITICAL'];
export const MEASUREMENT_SOURCES = [
    { id: 103, title: 'Pulseira', icon: 'watch' },
    { id: 104, title: 'Anel', icon: 'ring' }
];
/**
 * O catalogo do painel: quais tipos ele mostra, em que ordem, e quais tem faixa.
 *
 * O `label` NAO se escreve aqui - sai do `MEASUREMENT_NAMES`, que e a fonte unica
 * de nome. Esta lista responde outra pergunta: quais tipos entram na tela.
 */
export const MEASUREMENT_TYPES = [
    [103, true],
    [155, true],
    [153, true],
    [154, true],
    [102, true],
    [204, true],
    [201, false],
    [202, false],
    [203, false],
    /*
     * Frequencia cardiaca de ATIVIDADE: o relogio a grava a cada segundo de um
     * treino. Nao e a 153, que e a continua e mede repouso.
     *
     * Ganhou faixa em 13/09/2026 (migration `V20260913_1`), e por isso mostra
     * bolinha. A leitura das faixas dela e de INTENSIDADE e nao de alarme -
     * "otimo" e a zona aerobica de treino, e nao a frequencia mais baixa.
     */
    [205, true],
    [156, false]
].map(([id, hasStatus]) => ({ id, label: measurementLabelOf(id), hasStatus }));
export function measurementTypeOf(id) {
    return MEASUREMENT_TYPES.find((type) => type.id === id);
}
//# sourceMappingURL=measurements.js.map